import React from 'react';
import { Link } from 'react-router-dom';

import { formatCurrency } from '~libs/dataUtils';
import { formatHours, formatMoney, formatRate } from '~support-at-home/lib/budgetFormat';
import { splitOverLimit, withinLimit } from '~support-at-home/lib/overLimits';

import { Column, Row } from '~components/table-view';
import { DonutProps } from '~components/visualisation/donut/Donut';
import { FigureTileItem } from '~components/visualisation/figure-tile/FigureTileGroup';
import { TrackerLegendItem } from '~support-at-home/components/budgets/tracker/TrackerLegend';
import { TRACKER_FIGURE_COLOURS } from '~support-at-home/components/budgets/tracker/trackerPalette';
import {
  OverLap,
  overLegendRows,
  toCareManagementWithinSegments,
  toOverLapSegments,
  toWithinLimitSegments,
} from '~support-at-home/components/budgets/tracker/transforms';
import WholeMoney, { wholeMoney } from '~support-at-home/components/common/WholeMoney';

import {
  DashboardBudgetOverview,
  DashboardBudgetRowsWidget,
  DashboardBudgetsSummary,
  DashboardCareManagementWidget,
  DashboardCmWorker,
} from '~support-at-home/models/dashboard';

import { BreakdownKey, ListSide } from './dashboardFigures';
import { DASHBOARD_TEXT } from './dashboardText';

/** The text of each of the three Over/Under lists, by list. */
export const LIST_TEXT = {
  allocation: DASHBOARD_TEXT.allocation,
  utilisation: DASHBOARD_TEXT.utilisation,
  'care-management': DASHBOARD_TEXT.careManagementList,
} as const satisfies Record<BreakdownKey, unknown>;

const rightAligned = { header: 'cell-align-right', column: 'cell-align-right' };

/** Allocated = Available minus Residual: Available, Allocated and Residual (netted across budgets), then the rate. */
export const buildAllocationTiles = (summary: DashboardBudgetsSummary): FigureTileItem[] => {
  const copy = DASHBOARD_TEXT.summary;
  return [
    {
      key: 'available',
      label: copy.available.label,
      value: wholeMoney(summary.total_available_budget),
      description: copy.available.line,
      caption: { id: 'dashboard-allocation-available', content: copy.available.tooltip },
    },
    {
      key: 'allocated',
      label: copy.allocated.label,
      value: wholeMoney(summary.total_allocated_budget),
      description: copy.allocated.line,
      caption: { id: 'dashboard-allocation-allocated', content: copy.allocated.tooltip },
      joinBefore: 'equals',
    },
    {
      key: 'residual',
      label: copy.residual.label,
      value: wholeMoney(summary.total_available_budget - summary.total_allocated_budget),
      description: copy.residual.line,
      caption: { id: 'dashboard-allocation-residual', content: copy.residual.tooltip },
      joinBefore: 'plus',
    },
    // An empty cell, so the rate lines up with the Utilisation rate below (five cells each, TAV-3482)
    { key: 'allocation-blank', blank: true, joinBefore: 'space' },
    {
      key: 'allocation-rate',
      label: copy.allocationRate.label,
      value: formatRate(summary.allocation_rate),
      description: copy.allocationRate.line,
      caption: { id: 'dashboard-allocation-rate', content: copy.allocationRate.tooltip },
      joinBefore: 'divider',
    },
  ];
};

/**
 * Allocated = Booked + Claimed + Remaining (Remaining netted across budgets), then the rate: the Budget Tracker's
 * Utilisation row with the same signs (TAV-3482).
 */
export const buildUtilisationTiles = (
  overview: DashboardBudgetOverview,
  summary: DashboardBudgetsSummary,
): FigureTileItem[] => {
  const copy = DASHBOARD_TEXT.summary;
  return [
    {
      key: 'allocated',
      label: copy.allocated.label,
      value: wholeMoney(summary.total_allocated_budget),
      description: copy.allocated.line,
      caption: { id: 'dashboard-utilisation-allocated', content: copy.allocated.tooltip },
    },
    {
      key: 'booked',
      label: copy.booked.label,
      value: wholeMoney(overview.booked),
      description: copy.booked.line,
      caption: { id: 'dashboard-utilisation-booked', content: copy.booked.tooltip },
      joinBefore: 'equals',
    },
    {
      key: 'claimed',
      label: copy.claimed.label,
      value: wholeMoney(overview.claimed),
      description: copy.claimed.line,
      caption: { id: 'dashboard-utilisation-claimed', content: copy.claimed.tooltip },
      joinBefore: 'plus',
    },
    {
      key: 'remaining',
      label: copy.remaining.label,
      value: wholeMoney(overview.remaining),
      description: copy.remaining.line,
      caption: { id: 'dashboard-utilisation-remaining', content: copy.remaining.tooltip },
      joinBefore: 'plus',
    },
    {
      key: 'utilisation-rate',
      label: copy.utilisationRate.label,
      value: formatRate(summary.utilisation_rate),
      description: copy.utilisationRate.line,
      caption: { id: 'dashboard-utilisation-rate', content: copy.utilisationRate.tooltip },
      joinBefore: 'divider',
    },
  ];
};

export interface OverviewView {
  /** Undefined when there is nothing to draw: an empty ring with N/A. */
  donut?: DonutProps;
  legend: TrackerLegendItem[];
  /** The ring's total is over its limit: the card gets the amber outline. */
  over: boolean;
}

/**
 * The Company Budget Overview: the company's Booked and Claimed against the total Allocated, as one ring. The ring
 * stops at Allocated; when Booked and Claimed together are over it, the amount beyond is the second lap. The legend
 * has Booked, Claimed and Not yet booked within Allocated (claimed first, as the ring draws them), then, when over,
 * the amount beyond with its booked and claimed parts. Allocated in the centre and hover percentages of it.
 *
 * The ring is the company as a whole, not each budget added up within its own Allocated: one budget's overspend fills
 * another's unused share, so the lap shows only when the company total is over. A budget over its own agreed budget
 * is still in the banner and the Over-utilised list (TAV-3482).
 */
export const buildCompanyOverview = (data: {
  summary: Pick<DashboardBudgetsSummary, 'total_allocated_budget'>;
  overview: DashboardBudgetOverview;
  budgets: DashboardBudgetRowsWidget['budgets'];
}): OverviewView => {
  const copy = DASHBOARD_TEXT.overview;
  const allocated = data.summary.total_allocated_budget;
  const within = withinLimit(allocated, data.overview);
  const over = splitOverLimit(allocated, data.overview);
  const overTotal = over.total;
  const lap: OverLap = {
    key: 'company-over',
    label: copy.over.label,
    color: TRACKER_FIGURE_COLOURS.bookedOverAllocated,
  };

  return {
    donut:
      data.budgets.length > 0
        ? {
            segments: toWithinLimitSegments(within),
            overSegments: toOverLapSegments(overTotal, lap),
            centreValue: formatCurrency(allocated, 0),
            centreLabel: copy.centreLabel,
            percentBase: allocated,
            ariaLabel:
              `Allocated ${formatMoney(allocated)}: booked ${formatMoney(data.overview.booked)}, ` +
              `claimed ${formatMoney(data.overview.claimed)}, not yet booked ${formatMoney(within.unused)}${
                overTotal > 0 ? `, over the agreed budgets ${formatMoney(overTotal)}` : ''
              }`,
            dataCy: 'dashboard-company-overview-donut',
          }
        : undefined,
    legend: [
      {
        key: 'company-booked',
        label: copy.booked.label,
        value: within.booked,
        color: TRACKER_FIGURE_COLOURS.booked,
        caption: { id: 'dashboard-overview-booked', content: copy.booked.tooltip },
      },
      {
        key: 'company-claimed',
        label: copy.claimed.label,
        value: within.claimed,
        color: TRACKER_FIGURE_COLOURS.claimed,
        caption: { id: 'dashboard-overview-claimed', content: copy.claimed.tooltip },
      },
      {
        key: 'company-not-yet-booked',
        label: copy.notYetBooked.label,
        value: within.unused,
        color: TRACKER_FIGURE_COLOURS.remaining,
        caption: { id: 'dashboard-overview-not-yet-booked', content: copy.notYetBooked.tooltip },
      },
      // Booked, Claimed and Not yet booked above add up to Allocated (claimed first, as the ring); this row is the
      // rest, split into its booked and claimed parts, so nothing is counted twice (TAV-3482)
      ...overLegendRows(
        over,
        { ...lap, caption: { id: 'dashboard-overview-over', content: copy.over.tooltip } },
        copy.over,
      ),
    ],
    over: overTotal > 0,
  };
};

/**
 * The Care Management Overview: the same shape, Used against the allowance total, the allowance in the centre. Like
 * the Company Budget Overview it is the company total, so one budget's care management over its allowance fills
 * another's unused allowance and the lap shows only when the total is over. A budget over its own allowance is still
 * in the banner and the Over allowance list (TAV-3482).
 */
export const buildCareManagementOverview = (
  data: DashboardCareManagementWidget & DashboardBudgetRowsWidget,
): OverviewView => {
  const copy = DASHBOARD_TEXT.careManagement;
  const overview = data.case_management_overview;
  const within = withinLimit(overview.allowance, overview);
  const overTotal = splitOverLimit(overview.allowance, overview).total;
  const lap: OverLap = {
    key: 'company-cm-over',
    label: copy.over.label,
    color: TRACKER_FIGURE_COLOURS.careManagementOver,
  };

  return {
    donut:
      overview.budgets > 0
        ? {
            segments: toCareManagementWithinSegments(within),
            overSegments: toOverLapSegments(overTotal, lap),
            centreValue: formatCurrency(overview.allowance, 0),
            centreLabel: copy.centreLabel,
            percentBase: overview.allowance,
            ariaLabel:
              `Allowance ${formatMoney(overview.allowance)}: used ${formatMoney(overview.used)}, ` +
              `unused ${formatMoney(within.unused)}${
                overTotal > 0 ? `, over the allowances ${formatMoney(overTotal)}` : ''
              }`,
            dataCy: 'dashboard-cm-overview-donut',
          }
        : undefined,
    legend: [
      {
        key: 'company-cm-used',
        label: copy.used.label,
        value: overview.used,
        color: TRACKER_FIGURE_COLOURS.careManagementUsed,
        caption: {
          id: 'dashboard-cm-used',
          content: copy.used.tooltip(
            formatMoney(overview.booked),
            formatMoney(overview.claimed),
            formatMoney(overview.allowance),
          ),
        },
      },
      {
        key: 'company-cm-unused',
        label: copy.unused.label,
        value: within.unused,
        color: TRACKER_FIGURE_COLOURS.careManagementRemaining,
        caption: { id: 'dashboard-cm-unused', content: copy.unused.tooltip },
      },
    ],
    over: overTotal > 0,
  };
};

const { columns } = DASHBOARD_TEXT;

const textColumn = (id: string, copyOf: { header: string; caption: string }): Column => ({
  id,
  header: copyOf.header,
  caption: copyOf.caption,
  type: 'readonly',
});

const clientColumn = (): Column => textColumn('client_name', columns.client);

const budgetColumn = (): Column => ({
  ...textColumn('reference', columns.budget),
  renderReadOnly: (_value, row) => (
    <Link
      to={`/support-at-home/budgets/${row.budget_id}/tracker`}
      aria-label={columns.budget.linkLabel(row.reference as string, row.client_name as string)}
    >
      {row.reference}
    </Link>
  ),
});

/** Whole dollars, exact on hover; an amount over a limit shows as at least $1. */
const moneyColumn = (id: string, copyOf: { header: string; caption: string }, over = false): Column => ({
  ...textColumn(id, copyOf),
  classNames: rightAligned,
  renderReadOnly: (value) => <WholeMoney value={Number(value)} over={over} />,
});

/** A count (CM appointments): shown as it is. */
const countColumn = (id: string, copyOf: { header: string; caption: string }): Column => ({
  ...textColumn(id, copyOf),
  classNames: rightAligned,
});

const rateColumn = (copyOf: { header: string; caption: string }): Column => ({
  ...textColumn('rate', copyOf),
  classNames: rightAligned,
  deriveValue: (row) => formatRate(row.rate as number | null),
});

/** The widget's columns: Client, Budget and the amount the list ranks by. */
export const listColumns = (key: BreakdownKey, side: ListSide): Column[] => {
  const copy = LIST_TEXT[key][side];
  return [
    clientColumn(),
    budgetColumn(),
    ...(key === 'care-management' ? [countColumn('cm_appointments', columns.cmAppointments)] : []),
    moneyColumn('amount', { header: copy.amountHeader, caption: copy.amountCaption }, side === 'over'),
  ];
};

/** The full table's columns: the widget's figures plus the budget's own, and its rate. */
export const fullTableColumns = (key: BreakdownKey, side: ListSide): Column[] => {
  const copy = LIST_TEXT[key][side];
  const amount = moneyColumn('amount', { header: copy.amountHeader, caption: copy.amountCaption }, side === 'over');
  const overParts =
    side === 'over'
      ? [moneyColumn('booked_over', columns.bookedOver, true), moneyColumn('claimed_over', columns.claimedOver, true)]
      : [];

  if (key === 'allocation') {
    return [
      clientColumn(),
      budgetColumn(),
      textColumn('case_manager', columns.caseManager),
      textColumn('funding_type', columns.fundingType),
      moneyColumn('available', columns.available),
      moneyColumn('allocated', columns.allocated),
      amount,
    ];
  }

  const isCare = key === 'care-management';
  return [
    clientColumn(),
    budgetColumn(),
    textColumn('case_manager', columns.caseManager),
    textColumn('funding_type', columns.fundingType),
    isCare ? moneyColumn('allowance', columns.allowance) : moneyColumn('allocated', columns.allocated),
    moneyColumn('booked', columns.booked),
    moneyColumn('claimed', columns.claimed),
    ...overParts,
    ...(isCare ? [countColumn('cm_appointments', columns.cmAppointments)] : []),
    amount,
    rateColumn(isCare ? columns.usage : columns.utilisation),
  ];
};

const { workers } = DASHBOARD_TEXT;

export const workerColumns: Column[] = [
  textColumn('worker_name', workers.worker),
  {
    ...textColumn('cm_hours', workers.cmHours),
    classNames: rightAligned,
    renderReadOnly: (value) => formatHours(Number(value)),
  },
  {
    ...textColumn('hours_per_working_day', workers.hoursPerWorkingDay),
    classNames: rightAligned,
    renderReadOnly: (value) => (value === null || value === undefined ? 'n/a' : formatHours(Number(value))),
  },
];

/** One care management worker row, for the widget and its full table, so both show the same figures. */
export const mapWorkersToRows = (data: DashboardCmWorker[]): Row[] =>
  data.map((item) => ({
    id: item.worker_id,
    worker_name: item.worker_name,
    cm_hours: item.cm_hours,
    hours_per_working_day: item.hours_per_working_day,
    has_cm_hours: item.has_cm_hours,
  }));

export const getCurrentQuarter = (): number => Math.ceil((new Date().getMonth() + 1) / 3);

export const getCurrentYear = (): number => new Date().getFullYear();

export const getCurrentPeriod = (): { quarter: number; year: number } => {
  const now = new Date();
  return {
    quarter: Math.ceil((now.getMonth() + 1) / 3),
    year: now.getFullYear(),
  };
};
